// src/components/ConventionAdKit.js
import React, { useEffect, useRef, useState } from 'react';
import { DownloadSimple, Copy, Check, ArrowCounterClockwise } from '@phosphor-icons/react';
import {
  AD_WIDTH, AD_HEIGHT, AD_PREVIEW_SCALE, adToJPEG, altTextDocx, downloadBlob,
} from '../utils/conventionAd';

/**
 * Convention Ad Kit — live preview, editable copy, JPEG + alt-text downloads.
 *
 * Brand-agnostic: the page supplies the drawing and the palette, this
 * component supplies the form, the preview canvas, and the exports. Class
 * names are prefixed with `classPrefix` so each design system styles it with
 * its own tokens.
 *
 * props
 *   draw(ctx, content, surface) — paints the ad in 2400 × 3000 spec units and
 *     returns the visible text lines in reading order
 *   surfaces   — [{ id, name, swatch, swatchInk, ...anything draw needs }]
 *   defaults   — { eyebrow, headline, body, points: [..], url, link, tagline }
 *   fonts      — document.fonts.load() strings to wait for before the first draw
 *   slug       — filename stem, e.g. 'iiq-convention-ad'
 *   docTitle   — heading of the alt-text Word file
 *   altIntro   — first sentence of the alt text ("Advertisement for …")
 *   pointsLabel — form label for the points row
 *   classPrefix — 'ss-ad' | 'iiq-ad'
 */
const ConventionAdKit = ({
  draw, surfaces, defaults, fonts = [], slug, docTitle, altIntro,
  pointsLabel = 'Points', classPrefix,
}) => {
  const [content, setContent] = useState(defaults);
  const [surfaceId, setSurfaceId] = useState(surfaces[0].id);
  const [fontsReady, setFontsReady] = useState(false);
  const [visible, setVisible] = useState([]);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const canvasRef = useRef(null);

  const surface = surfaces.find((s) => s.id === surfaceId) || surfaces[0];
  const c = (name) => `${classPrefix}${name ? `-${name}` : ''}`;

  // Wait for the webfonts so the first preview isn't measured against a
  // fallback face and then never re-wrapped.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (typeof document !== 'undefined' && document.fonts) {
        try {
          await Promise.all(fonts.map((f) => document.fonts.load(f)));
          await document.fonts.ready;
        } catch (e) { /* fall back to system fonts */ }
      }
      if (!cancelled) setFontsReady(true);
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Preview: draw straight onto the on-page canvas at half scale
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.scale(AD_PREVIEW_SCALE, AD_PREVIEW_SCALE);
    setVisible(draw(ctx, content, surface));
  }, [draw, content, surface, fontsReady]);

  const update = (key) => (e) => setContent((prev) => ({ ...prev, [key]: e.target.value }));
  const updatePoint = (i) => (e) => setContent((prev) => {
    const points = [...prev.points];
    points[i] = e.target.value;
    return { ...prev, points };
  });

  const file = `${slug}-${surface.id}`;
  const alt = `${altIntro} ${visible.join(' ')}`;

  const handleJPEG = async () => {
    setBusy(true);
    try {
      const blob = await adToJPEG(draw, content, surface);
      if (blob) downloadBlob(blob, `${file}.jpg`);
    } finally {
      setBusy(false);
    }
  };

  const handleDocx = () => {
    downloadBlob(
      altTextDocx({ title: docTitle, alt, lines: visible, link: content.link || content.url }),
      `${file}-alt-text.docx`
    );
  };

  const handleCopyAlt = () => {
    navigator.clipboard.writeText(alt);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className={c()}>
      <div className={c('stage')}>
        <div className={c('frame')}>
          <canvas
            ref={canvasRef}
            className={c('canvas')}
            width={AD_WIDTH * AD_PREVIEW_SCALE}
            height={AD_HEIGHT * AD_PREVIEW_SCALE}
            role="img"
            aria-label={alt}
          />
        </div>
        <div className={c('spec')}>
          <span>2400 × 3000 px</span>
          <span>4 : 5</span>
          <span>7.2 MP</span>
          <span>300 DPI · 8 × 10 in</span>
        </div>
      </div>

      <div className={c('panel')}>
        <div className={c('field')}>
          <span className={c('label')}>Surface</span>
          <div className={c('surfaces')}>
            {surfaces.map((s) => (
              <button
                key={s.id}
                type="button"
                className={`${c('surface')}${s.id === surface.id ? ' active' : ''}`}
                aria-pressed={s.id === surface.id}
                onClick={() => setSurfaceId(s.id)}
              >
                <span
                  className={c('surface-chip')}
                  style={{ background: s.swatch, borderColor: s.swatchInk }}
                />
                {s.name}
              </button>
            ))}
          </div>
        </div>

        <label className={c('field')}>
          <span className={c('label')}>Eyebrow</span>
          <input className={c('input')} value={content.eyebrow} onChange={update('eyebrow')} />
        </label>

        <label className={c('field')}>
          <span className={c('label')}>Headline <em>line breaks are kept</em></span>
          <textarea className={c('input')} rows={3} value={content.headline} onChange={update('headline')} />
        </label>

        <label className={c('field')}>
          <span className={c('label')}>Body</span>
          <textarea className={c('input')} rows={4} value={content.body} onChange={update('body')} />
        </label>

        <div className={c('field')}>
          <span className={c('label')}>{pointsLabel}</span>
          <div className={`${c('row')} three`}>
            {content.points.map((p, i) => (
              <input key={i} className={c('input')} value={p} onChange={updatePoint(i)} />
            ))}
          </div>
        </div>

        <div className={c('row')}>
          <label className={c('field')}>
            <span className={c('label')}>Printed URL</span>
            <input className={c('input')} value={content.url} onChange={update('url')} />
          </label>
          <label className={c('field')}>
            <span className={c('label')}>Hyperlink</span>
            <input className={c('input')} value={content.link} onChange={update('link')} />
          </label>
        </div>

        <label className={c('field')}>
          <span className={c('label')}>Tagline</span>
          <input className={c('input')} value={content.tagline} onChange={update('tagline')} />
        </label>

        <div className={c('actions')}>
          <button type="button" className={`${c('btn')} primary`} onClick={handleJPEG} disabled={busy}>
            <DownloadSimple weight="fill" size={16} />
            {busy ? 'Rendering…' : 'JPEG · 2400 × 3000'}
          </button>
          <button type="button" className={c('btn')} onClick={handleDocx}>
            <DownloadSimple weight="fill" size={16} />
            Alt text · Word
          </button>
          <button type="button" className={c('btn')} onClick={handleCopyAlt}>
            {copied ? <Check weight="fill" size={16} /> : <Copy weight="fill" size={16} />}
            {copied ? 'Copied' : 'Copy alt text'}
          </button>
          <button type="button" className={`${c('btn')} ghost`} onClick={() => setContent(defaults)}>
            <ArrowCounterClockwise weight="fill" size={16} />
            Reset
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConventionAdKit;
